import { Fragment, useCallback, useEffect } from "react";

import { Overflow, ResponsiveDimension } from "../../../types";
import Overlay from "../Overlay";
import ContentBox from "./components/ContentBox";
import { MaxWidth, StandardContent } from "./types";

export type ModalProps = {
  children: React.ReactNode | StandardContent;
  partOfJourney?: boolean;
  inContainer?: boolean;
  onCloseClick?: () => void;
  padding?: ResponsiveDimension;
  maxWidth?: MaxWidth;
  fullscreenOnMobile?: boolean;
  overflowY?: Overflow;
  noTopImageContainer?: boolean;
  allowOutsideClick?: (e: MouseEvent | TouchEvent) => boolean;
};

export default function Modal({
  children,
  partOfJourney = false,
  inContainer = false,
  onCloseClick,
  padding = "xxlarge",
  maxWidth = "medium",
  fullscreenOnMobile = false,
  overflowY = "auto",
  noTopImageContainer = false,
  allowOutsideClick
}: ModalProps) {
  const preventPageScrollWhileModalOpen =
    !inContainer && document.body && document.body.style;

  const handleEscapeKey = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && onCloseClick) {
        onCloseClick();
      }
    },
    [onCloseClick]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleEscapeKey);

    if (preventPageScrollWhileModalOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);

      if (preventPageScrollWhileModalOpen) {
        document.body.style.overflow = "auto";
      }
    };
  }, [preventPageScrollWhileModalOpen]);

  const contentBoxProps = {
    inContainer,
    onCloseClick,
    padding,
    maxWidth,
    fullscreenOnMobile,
    overflowY,
    noTopImageContainer,
    allowOutsideClick
  };

  const contentBox = <ContentBox {...contentBoxProps}>{children}</ContentBox>;

  return partOfJourney ? (
    contentBox
  ) : (
    <Fragment>
      <Overlay
        onCloseClick={onCloseClick}
        position={inContainer ? "absolute" : "fixed"}
      />
      {contentBox}
    </Fragment>
  );
}
